import { Countdown, COLOR_STYLES, CATEGORY_COLOR_MAP } from '../types/database';
import { Pencil, Trash2, CalendarPlus } from 'lucide-react';
import { downloadICS } from '../utils/calendar';

// Color palette — all valid new colors
const colorPalette = [
  '#FF6B6B', // Coral
  '#E91E8C', // Rose
  '#6BAF92', // Sage
  '#F4A435', // Gold
  '#FF8C00', // Orange
  '#9B8EC4', // Lavender
  '#5B9BD5', // Sky
  '#E8A0BF', // Blush
]

// Soft category fallbacks (used when no color is chosen)
const categoryColors: Record<string, string> = {
  Birthday: '#FCE4EC',
  Holiday: '#FFF8E1',
  Travel: '#E8F5E9',
  School: '#EDE7F6',
  Personal: '#E3F2FD',
  Work: '#E8F5E9',
  Other: '#F3E5F5',
}

const legacyColorMap: Record<string, string> = {
  '#FF8C69': '#6BAF92', // old Peach → Sage
};

interface CountdownCardProps {
  countdown: Countdown;
  index: number;
  onShowDetail: (countdown: Countdown) => void;
  onEdit: (countdown: Countdown) => void;
  onDeleteRequest: (countdown: Countdown) => void;
}

export function CountdownCard({ countdown, index, onShowDetail, onEdit, onDeleteRequest }: CountdownCardProps) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const eventDate = new Date(countdown.event_date);
  eventDate.setHours(0, 0, 0, 0);
  
  const diffMs = eventDate.getTime() - today.getTime();
  const daysLeft = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  // Resolve legacy colors first
  const resolvedColor = legacyColorMap[countdown.color] || countdown.color;
  
  // Final card color — use saved color if it's in our palette, else category fallback
  const cardColor = (resolvedColor && colorPalette.includes(resolvedColor)) 
    ? resolvedColor 
    : (categoryColors[countdown.category] || '#F3E5F5');

  // Determine text contrast (simplistic check for dark colors in palette)
  const isDarkBg = ['#E91E8C', '#FF6B6B', '#1a3e54'].includes(cardColor);
  const textColor = isDarkBg ? 'text-white' : 'text-[#1a3e54]';
  const subTextColor = isDarkBg ? 'text-white/60' : 'text-[#1a3e54]/40';

  const isPast = daysLeft < 0;
  const isThisWeek = daysLeft >= 0 && daysLeft <= 7;

  return (
    <div 
      onClick={() => onShowDetail(countdown)}
      className="group relative rounded-[2.5rem] p-6 shadow-sm border-2 border-[#1a3e54]/5 hover:shadow-xl hover:border-[#1a3e54]/10 transition-shadow cursor-pointer"
      style={{ 
        animationDelay: `${index * 100}ms`,
        backgroundColor: cardColor
      }}
    >
      <div className="flex items-start gap-5">
        <div className="relative flex-shrink-0">
          <div className="w-20 h-20 rounded-3xl flex items-center justify-center text-4xl shadow-inner bg-white/20 backdrop-blur-sm group-hover:scale-110 transition-transform duration-500">
            {countdown.background_image_url ? (
              <img src={countdown.background_image_url} alt="" className="w-full h-full object-cover rounded-3xl" />
            ) : (
              countdown.emoji || '🎉'
            )}
          </div>
          {isThisWeek && !isPast && (
            <div className="absolute -top-2 -right-2 bg-[#9e142f] text-white text-[8px] font-black uppercase tracking-tighter px-2 py-1 rounded-full shadow-lg animate-pulse">
              Soon
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0 pt-1">
          <h3 className={`text-lg font-bold truncate mb-1 transition-colors leading-tight ${textColor}`}>
            {countdown.name}
          </h3>
          <div className={`flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.15em] ${subTextColor}`}>
            <span>{eventDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
          </div>
          <div className={`mt-3 inline-flex px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest bg-white/30 ${textColor}`}>
            {countdown.category}
          </div>
        </div>

        <div className="flex flex-col items-center gap-1 flex-shrink-0">
          <div className={`text-3xl font-black leading-none ${isPast ? 'opacity-20' : ''} ${textColor}`}>
            {Math.abs(daysLeft)}
          </div>
          <div className={`text-[10px] font-black uppercase tracking-widest ${subTextColor}`}>
            {isPast ? 'ago' : 'days'}
          </div>
        </div>
      </div>

      {/* Action Overlay */}
      <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <button 
          onClick={(e) => {
            e.stopPropagation();
            downloadICS(countdown);
          }}
          className="p-2 bg-white/90 backdrop-blur-sm rounded-xl text-[#1a3e54]/40 hover:text-[#1a3e54] hover:bg-white shadow-sm transition-all"
          title="Download Reminder"
        >
          <CalendarPlus className="w-4 h-4" />
        </button>
        <button 
          onClick={(e) => {
            e.stopPropagation();
            onEdit(countdown);
          }}
          className="p-2 bg-white/90 backdrop-blur-sm rounded-xl text-[#1a3e54]/40 hover:text-[#1a3e54] hover:bg-white shadow-sm transition-all"
          title="Edit Event"
        >
          <Pencil className="w-4 h-4" />
        </button>
        <button 
          onClick={(e) => {
            e.stopPropagation();
            onDeleteRequest(countdown);
          }}
          className="p-2 bg-white/90 backdrop-blur-sm rounded-xl text-[#9e142f]/40 hover:text-[#9e142f] hover:bg-white shadow-sm transition-all"
          title="Delete Event"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}